import React from "react";
import { Box, Dialog, IconButton, Link, Tab, Tabs, Typography } from "@mui/material";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { PillTag, formatDate, formatDuration, formatPersonName } from "./opportunityDisplay";
import { FIXED_TERM, inServiceTypeOrder } from "./opportunityOptions";

const ACCENT = "#E84610";
const DARK = "#483C3B";
const TEXT_SECONDARY = "rgba(0, 0, 0, 0.6)";

const Field = ({ label, children, fullWidth }) => (
  <Box sx={fullWidth ? { gridColumn: "1 / -1" } : undefined}>
    <Typography sx={{ fontSize: 13, lineHeight: "20px", color: TEXT_SECONDARY }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: 15, lineHeight: "24px", overflowWrap: "anywhere" }}>
      {children}
    </Typography>
  </Box>
);

const Card = ({ id, title, children }) => (
  <Box
    component="section"
    aria-labelledby={id}
    sx={{ border: "1px solid #E0E0E0", borderRadius: "8px", p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}
  >
    <Typography id={id} component="h3" sx={{ fontSize: 15, lineHeight: "24px", fontWeight: 600 }}>
      {title}
    </Typography>
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1.5 }}>{children}</Box>
  </Box>
);

// Read-only view of one opportunity from the list row (already populated); it makes no request.
// The Assigned Users tab stays disabled until piece 4 builds assignments.
const OpportunityDetailsDialog = ({ opportunity, onClose }) => {
  if (!opportunity) return null;
  const { opportunityId, name, status, client, description, partnerName, createdAt, dueDate, lead, owners } = opportunity;
  const { engagementDuration, expectedStartDate, expectedEndDate, deliveryModel } = opportunity;
  const serviceTypes = inServiceTypeOrder(opportunity.serviceTypes);
  const ownerNames = (owners || []).map(formatPersonName);

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby="opportunity-details-title"
      PaperProps={{
        sx: {
          width: 640,
          maxWidth: "calc(100% - 32px)",
          m: 2,
          p: 3,
          borderRadius: "8px",
          boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.3), 0px 2px 6px 2px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 30, gap: 2 }}>
          <Typography id="opportunity-details-title" component="h2" sx={{ fontSize: 18, lineHeight: "30px", fontWeight: 700 }}>
            {name || "—"}
          </Typography>
          <IconButton edge="end" size="small" aria-label="Close dialog" onClick={onClose} sx={{ color: DARK }}>
            <HighlightOffOutlinedIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: 15 }}>{opportunityId || "—"}</Typography>
          <PillTag data-testid="opportunity-status">{status}</PillTag>
        </Box>
      </Box>

      <Tabs
        value={0}
        sx={{
          minHeight: 40,
          "& .MuiTab-root": { minHeight: 40, px: 0, mr: 3, fontWeight: 500, fontSize: 14, color: TEXT_SECONDARY },
          "& .Mui-selected": { color: `${ACCENT} !important` },
          "& .MuiTabs-indicator": { backgroundColor: ACCENT },
        }}
      >
        <Tab label="OPPORTUNITY DETAILS" />
        <Tab label="ASSIGNED USERS" disabled />
      </Tabs>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Card id="opportunity-customer" title="Customer Information">
          <Field label="Company Name">{client?.name || "—"}</Field>
          <Field label="Website">
            {client?.website ? (
              <Link href={client.website} target="_blank" rel="noopener noreferrer" color="inherit" underline="hover">
                {client.website}
              </Link>
            ) : (
              "—"
            )}
          </Field>
          {partnerName && <Field label="Partner Company">{partnerName}</Field>}
        </Card>

        <Card id="opportunity-type" title="Opportunity Type">
          <Field label="Engagement Duration">{engagementDuration || "—"}</Field>
          <Field label="Duration">{formatDuration(opportunity)}</Field>
          <Field label="Delivery Model">{deliveryModel || "—"}</Field>
          <Field label="Service Types">{serviceTypes.length ? serviceTypes.join(", ") : "—"}</Field>
          {description && (
            <Field label="Description" fullWidth>
              {description}
            </Field>
          )}
        </Card>

        <Card id="opportunity-timeline" title="Timeline">
          <Field label="Created Date">{formatDate(createdAt)}</Field>
          <Field label="Due Date">{formatDate(dueDate)}</Field>
          <Field label="Expected Start Date">{formatDate(expectedStartDate)}</Field>
          {(engagementDuration === FIXED_TERM || !engagementDuration) && (
            <Field label="Expected End Date">{formatDate(expectedEndDate)}</Field>
          )}
        </Card>

        <Card id="opportunity-ownership" title="Ownership">
          <Field label="Lead">{formatPersonName(lead)}</Field>
          <Field label="Other Owners">{ownerNames.length ? ownerNames.join(", ") : "—"}</Field>
        </Card>
      </Box>
    </Dialog>
  );
};

export default OpportunityDetailsDialog;
